import { AlertCircle } from 'lucide-react'
import { Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'

import { useState } from 'react'

import { useReport } from '@/hooks/features/reports'
import { useBusiness } from '@/contexts/BusinessContext'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { PageHeader } from '@/components/atoms'
import { ChartCard, ChartTooltip, chartAxis, chartGrid, compactNumber } from '@/components/dashboard/chart-kit'
import { StatCard } from '@/components/dashboard/StatCard'
import { ReportToolbar } from '@/components/reports/ReportToolbar'
import { trendOf, useReportRange } from '@/components/reports/useReportRange'

import type { CategorySalesRow, PaymentMethodRow, ProductSalesRow, SalesOverview } from '@/types/reports'

const label = (method: string) => method.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())

/**
 * Sales: net sales, orders, average order and buying customers against the previous period of the same length; net
 * sales over time; best-selling products (by sales or units), categories and payment methods. Excel downloads use the
 * same dates and rules.
 */
const SalesReport = () => {
  const { formatCurrency } = useBusiness()
  const { preset, range, onPresetChange, onRangeChange } = useReportRange()
  const [productSort, setProductSort] = useState<'revenue' | 'quantity'>('revenue')

  const overview = useReport<SalesOverview>('overview', { ...range })
  const products = useReport<ProductSalesRow[]>('products', { ...range, sort: productSort, limit: 15 })
  const categories = useReport<CategorySalesRow[]>('categories', { ...range })
  const methods = useReport<PaymentMethodRow[]>('payment-methods', { ...range })

  const o = overview.data
  const note = o ? `vs ${o.previous_range.from} – ${o.previous_range.to}` : undefined

  return (
    <div className="space-y-4">
      <PageHeader title="Sales report" description="Delivered online orders and completed POS sales, net of refunds, in your store's time zone." />
      <ReportToolbar
        preset={preset}
        range={range}
        onPresetChange={onPresetChange}
        onRangeChange={onRangeChange}
        downloads={[
          { name: 'orders', label: 'Orders (Excel)' },
          { name: 'sales-by-product', label: 'Products (Excel)' },
          { name: 'sales-by-payment-method', label: 'Payment methods (Excel)' },
        ]}
      />

      {overview.isError && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Could not load the report</AlertTitle>
          <AlertDescription>{overview.error.message}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {o ? (
          <>
            <StatCard title="Net sales" value={formatCurrency(o.totals.revenue)} {...trendOf(o.changes.revenue)} note={note} spark={o.series.map((s) => s.revenue)} />
            <StatCard title="Orders" value={o.totals.orders.toLocaleString()} {...trendOf(o.changes.orders)} note={note} />
            <StatCard title="Average order" value={formatCurrency(o.totals.average_order_value)} {...trendOf(o.changes.average_order_value)} note={note} />
            <StatCard title="Buying customers" value={o.totals.customers.toLocaleString()} {...trendOf(o.changes.customers)} note={note} />
          </>
        ) : (
          Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-24" />)
        )}
      </div>

      {o && (
        <ChartCard
          title="Net sales over time"
          value={formatCurrency(o.totals.revenue)}
          subheader={`${o.range.from} – ${o.range.to} · by ${o.interval} · ${o.range.timezone}`}
          empty={!o.series.some((s) => s.revenue)}
          table={{ columns: ['Period', 'Net sales'], rows: o.series.map((s) => [s.date, formatCurrency(s.revenue)]) }}
        >
          <AreaChart data={o.series} margin={{ left: 0, right: 8 }}>
            <defs>
              <linearGradient id="salesReportFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.16} />
                <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...chartGrid} />
            <XAxis dataKey="date" {...chartAxis} minTickGap={24} />
            <YAxis {...chartAxis} tickFormatter={compactNumber} width={48} />
            <Tooltip content={<ChartTooltip format={(v) => formatCurrency(Number(v))} />} />
            <Area type="monotone" dataKey="revenue" name="Net sales" stroke="hsl(var(--chart-1))" fill="url(#salesReportFill)" strokeWidth={2} />
          </AreaChart>
        </ChartCard>
      )}

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CardTitle className="w-fit text-sm font-semibold underline decoration-faint decoration-dotted underline-offset-4">Best-selling products</CardTitle>
          <Tabs value={productSort} onValueChange={(v) => setProductSort(v as 'revenue' | 'quantity')}>
            <TabsList>
              <TabsTrigger value="revenue">By sales</TabsTrigger>
              <TabsTrigger value="quantity">By units</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {products.isLoading ? (
            <Skeleton className="h-48" />
          ) : products.isError ? (
            <p className="text-sm text-error">{products.error.message}</p>
          ) : !products.data?.length ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No sales in these dates.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Units</TableHead>
                  <TableHead className="text-right">Net sales</TableHead>
                  <TableHead className="text-right">Est. profit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.data.map((p) => (
                  <TableRow key={p.product_id}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-muted-foreground">{p.category || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.quantity.toLocaleString()}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(p.revenue)}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.cost ? formatCurrency(p.profit) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <p className="mt-3 text-xs text-muted-foreground">Profit uses the purchase cost saved with each sale; “—” when no cost was recorded.</p>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {[
          { title: 'Sales by category', query: categories, rows: (categories.data ?? []).map((c) => [c.name, c.quantity.toLocaleString(), formatCurrency(c.revenue)]), cols: ['Category', 'Units', 'Net sales'] },
          { title: 'Sales by payment method', query: methods, rows: (methods.data ?? []).map((m) => [label(m.method), m.orders.toLocaleString(), formatCurrency(m.revenue)]), cols: ['Method', 'Orders', 'Net sales'] },
        ].map((block) => (
          <Card key={block.title}>
            <CardHeader>
              <CardTitle className="w-fit text-sm font-semibold underline decoration-faint decoration-dotted underline-offset-4">{block.title}</CardTitle>
            </CardHeader>
            <CardContent>
              {block.query.isLoading ? (
                <Skeleton className="h-40" />
              ) : block.query.isError ? (
                <p className="text-sm text-error">{block.query.error.message}</p>
              ) : !block.rows.length ? (
                <p className="py-6 text-center text-sm text-muted-foreground">No sales in these dates.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      {block.cols.map((c, i) => (
                        <TableHead key={c} className={i ? 'text-right' : undefined}>
                          {c}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {block.rows.map((r) => (
                      <TableRow key={r[0]}>
                        {r.map((cell, i) => (
                          <TableCell key={i} className={i ? 'text-right tabular-nums' : 'font-medium'}>
                            {cell}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

export default SalesReport
